import { useMemo } from 'react';
import Vendor from 'data/Vendor';
import { BusinessUnit } from 'data/BusinessUnit';
import { GENERAL_CONSTANTS } from 'constants/general';

interface UseVendorBusinessUnitSelectionProps {
  vendorsList: Vendor[];
  businessUnitList: BusinessUnit[];
  selectedVendor: Vendor | null;
  selectedBusinessUnit: BusinessUnit | null;
}

interface UseVendorBusinessUnitSelectionReturn {
  vendorOptions: Array<{ value: string; id: string }>;
  businessUnitOptions: Array<{ value: string; id: string }>;
  filteredBusinessUnits: BusinessUnit[];
  brandLogoUrl: string | null;
}

/**
 * Custom hook for managing vendor and business unit selection options
 * 
 * This hook provides:
 * - Memoized vendor options for dropdown
 * - Filtered business unit options based on selected vendor
 * - Brand logo URL extraction
 * - Performance optimized with proper memoization
 * 
 * @param props - Configuration object with vendor and business unit data
 * @returns Selection options and derived data for dropdowns
 */
export const useVendorBusinessUnitSelection = ({
  vendorsList,
  businessUnitList,
  selectedVendor,
  selectedBusinessUnit
}: UseVendorBusinessUnitSelectionProps): UseVendorBusinessUnitSelectionReturn => {

  // Memoized vendor options for dropdown
  const vendorOptions = useMemo(() => {
    return vendorsList.map(vendor => ({
      value: vendor.name,
      id: vendor.id.toString()
    }));
  }, [vendorsList]);

  // Filter business units based on selected vendor
  const filteredBusinessUnits = useMemo(() => {
    return selectedVendor?.id
      ? businessUnitList.filter(unit => unit.vendorId === selectedVendor.id)
      : businessUnitList;
  }, [selectedVendor?.id, businessUnitList]);

  // Memoized business unit options for dropdown
  const businessUnitOptions = useMemo(() => {
    const baseOptions = filteredBusinessUnits.map(unit => ({
      value: unit.name,
      id: unit.id?.toString() || '',
      disabled: !!unit.deletedOn
    }));

    // Add "All" option to allow users to select all business units
    return [
      { value: GENERAL_CONSTANTS.ALL, id: 'all' },
      ...baseOptions
    ];
  }, [filteredBusinessUnits]);

  // Extract brand logo URL from selected business unit
  const brandLogoUrl = useMemo(() => {
    const selectedBU = filteredBusinessUnits.find(bu => bu.id === selectedBusinessUnit?.id);
    return selectedBU?.brand?.media?.[0]?.path
      ? `${process.env.REACT_APP_MEDIA_URL}${selectedBU.brand.media[0].path}`
      : null;
  }, [filteredBusinessUnits, selectedBusinessUnit?.id]);

  return {
    vendorOptions,
    businessUnitOptions,
    filteredBusinessUnits,
    brandLogoUrl
  };
};